import React from "react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { inks, paper } from "../viz";

/**
 * The zoom instrument: a main track that rescales its viewport as the reader
 * drills deeper, plus a thin full-day minimap rail that appears only once the
 * viewport is actually zoomed (at full width the two rails would just repeat
 * each other). Depth becomes something you see — segments spread apart, axis
 * times change — instead of a label.
 *
 * Segments are REAL time blocks mapped from fetched data (session/stretch/
 * sitting start+end). Nothing here invents time: no data → the instrument
 * renders nothing at all.
 *
 * Motion (the .55s zoom on left/width) lives in WowMotion's .tgw-zoomseg /
 * .tgw-zoomwin classes so prefers-reduced-motion is honoured by the same CSS
 * that governs every other tgw animation.
 */

const SR_ONLY = {
  position: "absolute", width: 1, height: 1, overflow: "hidden",
  clip: "rect(0,0,0,0)", whiteSpace: "nowrap",
};

/** Minutes since midnight → "H:MM". */
function fmtClock(m) {
  let h = Math.floor(m / 60);
  let mm = Math.round(m % 60);
  if (mm === 60) { h += 1; mm = 0; }
  h = ((h % 24) + 24) % 24;
  return `${h}:${String(mm).padStart(2, "0")}`;
}

/** Minutes → "1h 30m" / "45m". */
function fmtDur(min) {
  const m = Math.round(min);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h && mm) return `${h}h ${String(mm).padStart(2, "0")}m`;
  if (h) return `${h}h`;
  return `${mm}m`;
}

/**
 * Round-clock ticks for the current window: 6:39–23:34 gets 7:00 / 11:00 /
 * 15:00 / 19:00 / 23:00, not five odd minute-marks. Step picks the smallest
 * round interval that yields at most ~6 ticks.
 */
function niceTicks(vp) {
  const span = vp[1] - vp[0];
  const steps = [5, 10, 15, 30, 60, 120, 180, 240, 360];
  const step = steps.find((s) => span / s <= 6) || 360;
  const ticks = [];
  for (let t = Math.ceil(vp[0] / step) * step; t <= vp[1]; t += step) ticks.push(t);
  return ticks;
}

export function ZoomInstrument({
  segments, viewport, focus, onZoomOut, hotId, onHotChange,
  hotIds, legend, heading = "The day on a line",
}) {
  const t = useDashTheme();
  const k = inks(t);
  const ppr = paper(t);

  const list = Array.isArray(segments)
    ? segments.filter((s) => s && Number.isFinite(s.s) && Number.isFinite(s.e) && s.e > s.s)
    : [];
  if (!list.length) return null;

  const inkOf = (name) => k[name] || k.blue;

  // Full-day frame: the span of every segment, padded ~4% so edge blocks
  // never kiss the rail's ends.
  const minS = Math.min(...list.map((s) => s.s));
  const maxE = Math.max(...list.map((s) => s.e));
  const dayPad = Math.max(4, (maxE - minS) * 0.04);
  const day = [minS - dayPad, maxE + dayPad];
  const daySpan = day[1] - day[0];

  const vp = Array.isArray(viewport) && viewport.length === 2 && viewport[1] > viewport[0]
    ? viewport
    : day;
  const span = vp[1] - vp[0];
  const zoomed = span < daySpan * 0.98;

  const dayPct = (x) => ((x - day[0]) / daySpan) * 100;
  const vpPct = (x) => ((x - vp[0]) / span) * 100;

  const ticks = niceTicks(vp);
  const isHot = (id) =>
    (hotId != null && id === hotId) ||
    (Array.isArray(hotIds) && hotIds.indexOf(id) !== -1);
  const anyHot = hotId != null || (Array.isArray(hotIds) && hotIds.length > 0);

  return (
    <div style={{ width: "100%" }}>
      {/* Heading + honest span note. */}
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        marginBottom: 8, gap: 12,
      }}>
        <span style={{
          ...t.font.microLabel,
          letterSpacing: "0.05em", color: t.c.textTertiary,
        }}>{heading}</span>
        <span style={{
          fontSize: 11, color: t.c.textTertiary,
          fontVariantNumeric: "tabular-nums",
        }}>
          {zoomed
            ? `showing ${fmtClock(vp[0])} – ${fmtClock(vp[1])}`
            : `first activity ${fmtClock(minS)} · last ${fmtClock(maxE)}`}
        </span>
      </div>

      {/* Minimap — the whole day. Only shown once the track is zoomed, so the
          two rails never repeat each other at full width. */}
      {zoomed ? (
        <div style={{
          position: "relative", height: 8, borderRadius: 4,
          background: ppr.well, marginBottom: 10,
        }}>
          {list.map((seg) => (
            <div
              key={`mini-${seg.id}`}
              aria-hidden="true"
              style={{
                position: "absolute", top: 1, bottom: 1, borderRadius: 3, opacity: 0.55,
                left: `${dayPct(seg.s)}%`,
                width: `${Math.max(0.4, dayPct(seg.e) - dayPct(seg.s))}%`,
                background: inkOf(seg.ink),
              }}
            />
          ))}
          <div
            className="tgw-zoomwin"
            aria-hidden="true"
            style={{
              position: "absolute", top: -3, bottom: -3, borderRadius: 5,
              border: `1.5px solid ${k.blue}`, background: `${k.blue}18`,
              left: `${dayPct(vp[0])}%`,
              width: `${Math.max(1, dayPct(vp[1]) - dayPct(vp[0]))}%`,
              pointerEvents: "none", boxSizing: "border-box",
            }}
          />
          {onZoomOut ? (
            <button
              type="button"
              onClick={onZoomOut}
              aria-label="Zoom out to the whole day"
              title="Zoom out to the whole day"
              style={{
                position: "absolute", left: 0, right: 0, top: -4, bottom: -4,
                width: "100%", background: "none", border: 0, padding: 0,
                cursor: "zoom-out",
              }}
            />
          ) : null}
        </div>
      ) : null}

      {/* Main track — rescaled to the viewport; drilling animates as a zoom. */}
      <div style={{
        position: "relative", height: 28, borderRadius: 8,
        background: ppr.well, overflow: "hidden",
      }}>
        {list.map((seg) => {
          const color = inkOf(seg.ink);
          const dimFocus = Array.isArray(focus) && focus.length === 2
            && (seg.e <= focus[0] || seg.s >= focus[1]);
          const hot = isHot(seg.id);
          const dim = dimFocus || (anyHot && !hot);
          const widthPct = Math.max(0.4, vpPct(seg.e) - vpPct(seg.s));
          const showDur = widthPct > 9;
          return (
            <div
              key={seg.id}
              role="img"
              aria-label={seg.label || ""}
              title={seg.label || ""}
              className="tgw-zoomseg"
              onMouseEnter={() => onHotChange?.(seg.id)}
              onMouseLeave={() => onHotChange?.(null)}
              style={{
                position: "absolute", top: 3, bottom: 3, borderRadius: 5,
                left: `${vpPct(seg.s)}%`,
                width: `${widthPct}%`,
                background: color,
                opacity: dim ? (dimFocus ? 0.16 : 0.35) : hot ? 1 : 0.85,
                boxShadow: hot ? `0 0 0 2px ${ppr.card}, 0 0 0 3.5px ${color}` : "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {showDur ? (
                <span aria-hidden="true" style={{
                  fontSize: 10, fontWeight: 700, color: "#fff",
                  fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                  opacity: hot ? 0.95 : 0.75, letterSpacing: "0.02em",
                }}>
                  {fmtDur(seg.e - seg.s)}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Axis — round clock marks for the current window, placed where they
          actually fall instead of five evenly spread odd times. */}
      <div style={{ position: "relative", height: 16, marginTop: 5 }}>
        {ticks.map((tick) => {
          const p = Math.min(98, Math.max(2, vpPct(tick)));
          return (
            <span key={tick} style={{
              position: "absolute", left: `${p}%`, transform: "translateX(-50%)",
              fontSize: 10.5, color: t.c.textTertiary,
              fontVariantNumeric: "tabular-nums", fontWeight: 650, whiteSpace: "nowrap",
            }}>{fmtClock(tick)}</span>
          );
        })}
      </div>

      {/* Legend — who the colors are, with their real totals. */}
      {Array.isArray(legend) && legend.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 8 }}>
          {legend.map((item, i) => (
            <span key={`${item.label}-${i}`} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 12, color: t.c.textSecondary,
            }}>
              <i aria-hidden="true" style={{
                width: 8, height: 8, borderRadius: 99, background: inkOf(item.ink),
              }} />
              {item.label}
            </span>
          ))}
        </div>
      ) : null}

      <span aria-live="polite" style={SR_ONLY}>
        {`Timeline showing ${fmtClock(vp[0])} to ${fmtClock(vp[1])}.`}
      </span>
    </div>
  );
}

export default ZoomInstrument;
